import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogContent,
  Chip,
  Stack,
} from '@mui/material';
import {
  AddComment as AddCommentIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrackChanges as TrackChangesIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

interface SharedContract {
  id: string;
  originalText: string;
  createdAt: Date;
  comments: Array<{
    id: string;
    text: string;
    selectedText: string;
    start: number;
    end: number;
    comment: string;
    timestamp: Date;
  }>;
  revisions: Array<{
    id: string;
    originalText: string;
    newText: string;
    reason: string;
    start: number;
    end: number;
    timestamp: Date;
    accepted: boolean | null;
    authorSource?: 'original' | 'shared';
  }>;
}

const SharedContractPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [contract, setContract] = useState<SharedContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isAddingRevision, setIsAddingRevision] = useState(false);
  const [selectedText, setSelectedText] = useState<string>('');
  const [selectedTextRange, setSelectedTextRange] = useState<{start: number, end: number} | null>(null);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [newRevisionText, setNewRevisionText] = useState('');
  const [newRevisionReason, setNewRevisionReason] = useState('');
  const [confirmAcceptRevision, setConfirmAcceptRevision] = useState<string | null>(null);
  const [rejectRevisionId, setRejectRevisionId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRevisionDetail, setShowRevisionDetail] = useState<string | null>(null);
  const [highlightedText, setHighlightedText] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);
  const contractPreviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Načtení dat smlouvy z localStorage
    const loadContract = () => {
      try {
        const storedData = localStorage.getItem(`shared-contract-${shareId}`);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setContract({
            ...parsedData,
            createdAt: new Date(parsedData.createdAt),
            comments: parsedData.comments || [],
            revisions: parsedData.revisions || []
          });
          if (loading) {
            setLoading(false);
          }
        } else {
          setError('Smlouva nebyla nalezena. Zkontrolujte, zda je odkaz správný.');
          setLoading(false);
        }
      } catch (err) {
        setError('Chyba při načítání smlouvy.');
        setLoading(false);
      }
    };

    if (shareId) {
      loadContract();
      
      // Nastavíme interval pro pravidelné načítání změn (každé 2 sekundy)
      const interval = setInterval(() => {
        loadContract();
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [shareId, loading]);

  // Uložení změn do localStorage
  const saveContract = (updatedContract: SharedContract) => {
    localStorage.setItem(`shared-contract-${shareId}`, JSON.stringify(updatedContract));
    setContract(updatedContract);
  };

  const handleAddComment = () => {
    if (!selectedText || !newCommentText.trim() || !contract) return;

    const newComment = {
      id: Date.now().toString(),
      text: newCommentText,
      selectedText: selectedText,
      start: selectedTextRange?.start || 0,
      end: selectedTextRange?.end || 0,
      comment: newCommentText,
      timestamp: new Date()
    };

    const updatedContract = {
      ...contract,
      comments: [...contract.comments, newComment]
    };

    saveContract(updatedContract);
    setShowCommentDialog(false);
    setNewCommentText('');
    setSelectedText('');
    setSelectedTextRange(null);
    setIsAddingComment(false);
  };

  const handleAddRevision = () => {
    if (!selectedText || !newRevisionText.trim() || !newRevisionReason.trim() || !contract) return;

    const newRevision = {
      id: Date.now().toString(),
      originalText: selectedText,
      newText: newRevisionText,
      reason: newRevisionReason,
      start: selectedTextRange?.start || 0,
      end: selectedTextRange?.end || 0,
      timestamp: new Date(),
      accepted: null,
      authorSource: 'shared' as const
    };

    const updatedContract = {
      ...contract,
      revisions: [...contract.revisions, newRevision]
    };

    saveContract(updatedContract);
    setShowRevisionDialog(false);
    setNewRevisionText('');
    setNewRevisionReason('');
    setSelectedText('');
    setSelectedTextRange(null);
    setIsAddingRevision(false);
  };

  const handleCommentClick = (comment: any) => {
    if (!contractPreviewRef.current) return;
    
    setHighlightedText(''); // Clear highlighting to preserve revision display
    
    setTimeout(() => {
      const previewElement = contractPreviewRef.current;
      if (previewElement) {
        const textToFind = comment.selectedText.substring(0, Math.min(30, comment.selectedText.length)).toLowerCase();
        const previewContent = (previewElement.textContent || '').toLowerCase();
        const textIndex = previewContent.indexOf(textToFind);
        
        if (textIndex >= 0) {
          const scrollRatio = textIndex / previewContent.length;
          const scrollPosition = Math.max(0, (scrollRatio * previewElement.scrollHeight) - 100);
          
          previewElement.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  };
  
  const handleRevisionClick = (revision: any) => {
    if (!contractPreviewRef.current) return;
    
    setHighlightedText(''); // Clear highlighting to preserve revision display
    
    setTimeout(() => {
      const previewElement = contractPreviewRef.current;
      if (previewElement) {
        const textToFind = revision.originalText.substring(0, Math.min(30, revision.originalText.length)).toLowerCase();
        const previewContent = (previewElement.textContent || '').toLowerCase();
        const textIndex = previewContent.indexOf(textToFind);
        
        if (textIndex >= 0) {
          const scrollRatio = textIndex / previewContent.length;
          const scrollPosition = Math.max(0, (scrollRatio * previewElement.scrollHeight) - 100);
          
          previewElement.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  };

  const handleDeleteComment = (commentId: string) => {
    if (!contract) return;
    const updatedContract = {
      ...contract,
      comments: contract.comments.filter(c => c.id !== commentId)
    };
    saveContract(updatedContract);
  };

  const handleAcceptRevision = (revisionId: string) => {
    if (!contract) return;
    const updatedContract = {
      ...contract,
      revisions: contract.revisions.map(r =>
        r.id === revisionId ? { ...r, accepted: true } : r
      )
    };
    saveContract(updatedContract);
    setConfirmAcceptRevision(null);
  };

  const handleRejectRevision = (revisionId: string, reason: string) => {
    if (!contract) return;
    const updatedContract = {
      ...contract,
      revisions: contract.revisions.map(r =>
        r.id === revisionId ? { ...r, accepted: false, reason } : r
      )
    };
    saveContract(updatedContract);
    setRejectRevisionId(null);
    setRejectReason('');
  };

  const copyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Načítání...</Typography>
      </Container>
    );
  }

  if (error || !contract) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Smlouva nebyla nalezena'}</Alert>
      </Container>
    );
  }

  // Render textu s poznámkami a revizemi (stejná logika jako v ContractAnalysisPage)
  const renderContractText = () => {
    let workingText = contract.originalText;
    const positionOffsets: Array<{start: number, end: number, offset: number}> = [];
    
    // Aplikujeme přijaté revize
    const acceptedRevisions = contract.revisions.filter(r => r.accepted === true).sort((a, b) => a.start - b.start);
    acceptedRevisions.forEach(revision => {
      const lengthDiff = revision.newText.length - (revision.end - revision.start);
      workingText = workingText.substring(0, revision.start) + 
                    revision.newText + 
                    workingText.substring(revision.end);
      positionOffsets.push({start: revision.start, end: revision.end, offset: lengthDiff});
    });
    
    // Upravíme pozice
    const adjustPosition = (pos: number): number => {
      let adjusted = pos;
      positionOffsets.forEach(offset => {
        if (pos > offset.end) {
          adjusted += offset.offset;
        }
      });
      return adjusted;
    };
    
    if (contract.comments.length === 0 && contract.revisions.filter(r => r.accepted !== true).length === 0) {
      return workingText;
    }
    
    const segments: Array<{
      text: string, 
      type: 'normal' | 'comment' | 'revision',
      id?: string,
      newText?: string,
      reason?: string,
      accepted?: boolean | null
    }> = [];
    let lastIndex = 0;
    
    const allMarks = [
      ...contract.comments.map(c => ({ 
        ...c, 
        type: 'comment' as const,
        adjustedStart: adjustPosition(c.start),
        adjustedEnd: adjustPosition(c.end)
      })),
      ...contract.revisions.filter(r => r.accepted !== true).map(r => ({ 
        ...r, 
        type: 'revision' as const,
        adjustedStart: adjustPosition(r.start),
        adjustedEnd: adjustPosition(r.end)
      }))
    ].sort((a, b) => a.adjustedStart - b.adjustedStart);
    
    allMarks.forEach(mark => {
      if (lastIndex < mark.adjustedStart) {
        segments.push({
          text: workingText.substring(lastIndex, mark.adjustedStart),
          type: 'normal'
        });
      }
      
      if (mark.type === 'comment') {
        segments.push({
          text: workingText.substring(mark.adjustedStart, mark.adjustedEnd),
          type: 'comment',
          id: mark.id
        });
      } else if (mark.type === 'revision') {
        segments.push({
          text: workingText.substring(mark.adjustedStart, mark.adjustedEnd),
          type: 'revision',
          id: mark.id,
          newText: mark.newText,
          reason: mark.reason,
          accepted: mark.accepted
        });
      }
      
      lastIndex = mark.adjustedEnd;
    });
    
    if (lastIndex < workingText.length) {
      segments.push({
        text: workingText.substring(lastIndex),
        type: 'normal'
      });
    }

    return (
      <>
        {segments.map((segment, index) => {
          if (segment.type === 'comment') {
            return (
              <Box
                key={index}
                component="span"
                sx={{
                  backgroundColor: '#fffbf0',
                  borderBottom: '2px solid #ffc107',
                  padding: '2px 0',
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#fff3cd'
                  }
                }}
                title={contract.comments.find(c => c.id === segment.id)?.comment}
              >
                {segment.text}
              </Box>
            );
          } else if (segment.type === 'revision') {
            return (
              <span key={index} data-revision-id={segment.id}>
                {segment.accepted === false ? (
                  <span>{segment.text}</span>
                ) : (
                  <>
                    <Box
                      component="span"
                      data-revision-id={segment.id}
                      className="revision-strikethrough"
                      sx={{
                        textDecoration: 'line-through',
                        color: '#d32f2f',
                        backgroundColor: '#ffebee',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        transition: 'all 0.5s ease-out'
                      }}
                    >
                      {segment.text}
                    </Box>
                    <Box
                      component="span"
                      data-revision-id={segment.id}
                      className="revision-replacement"
                      sx={{
                        color: '#2e7d32',
                        backgroundColor: '#e8f5e9',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        marginLeft: '4px',
                        fontWeight: 500,
                        transition: 'all 0.5s ease-out'
                      }}
                    >
                      {segment.newText}
                    </Box>
                  </>
                )}
              </span>
            );
          } else {
            return <span key={index}>{segment.text}</span>;
          }
        })}
      </>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: '#f5f5f7',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            📄 Sdílený náhled smlouvy
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip 
              icon={<ShareIcon />} 
              label="Sdílený dokument" 
              color="success" 
              variant="filled"
              sx={{ color: '#000 !important' }}
            />
            <Button
              startIcon={<CopyIcon />}
              variant="outlined"
              onClick={copyLink}
              sx={{ color: '#000 !important' }}
            >
              {linkCopied ? 'Zkopírováno!' : 'Kopírovat odkaz'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'flex-start',
          position: 'relative'
        }}>
          {/* Contract Preview - Main Content */}
          <Box sx={{ 
            flex: (contract.comments.length > 0 || contract.revisions.length > 0) ? '1 1 50%' : '1 1 100%',
            maxWidth: { lg: (contract.comments.length > 0 || contract.revisions.length > 0) ? 'calc(100% - 318px)' : '100%' },
            width: { xs: '100%', lg: (contract.comments.length > 0 || contract.revisions.length > 0) ? 'calc(100% - 318px)' : '100%' },
            minWidth: 0,
            transition: 'all 0.5s ease-in-out'
          }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📄 Náhled smlouvy
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={isAddingComment ? "Zrušit přidávání poznámky" : "Přidat poznámku k textu"}>
                    <Button
                      startIcon={<AddCommentIcon />}
                      onClick={() => {
                        setIsAddingComment(!isAddingComment);
                        setIsAddingRevision(false);
                        if (!isAddingComment) {
                          setSelectedText('');
                          setSelectedTextRange(null);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      color={isAddingComment ? "warning" : "primary"}
                      sx={{ 
                        color: '#000 !important',
                        bgcolor: isAddingComment ? 'rgba(255, 193, 7, 0.1)' : 'transparent',
                        borderColor: isAddingComment ? '#ffc107' : 'rgba(0, 0, 0, 0.23)',
                        '&:hover': {
                          bgcolor: isAddingComment ? 'rgba(255, 193, 7, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                          borderColor: isAddingComment ? '#ffc107' : 'rgba(0, 0, 0, 0.23)'
                        }
                      }}
                    >
                      {isAddingComment ? 'Vyberte text' : 'Přidat poznámku'}
                    </Button>
                  </Tooltip>
                  <Tooltip title={isAddingRevision ? "Zrušit přidávání revize" : "Přidat revizi k textu"}>
                    <Button
                      startIcon={<TrackChangesIcon />}
                      onClick={() => {
                        setIsAddingRevision(!isAddingRevision);
                        setIsAddingComment(false);
                        if (!isAddingRevision) {
                          setSelectedText('');
                          setSelectedTextRange(null);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      color={isAddingRevision ? "info" : "primary"}
                      sx={{ 
                        color: '#000 !important',
                        bgcolor: isAddingRevision ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                        borderColor: isAddingRevision ? '#2196f3' : 'rgba(0, 0, 0, 0.23)',
                        '&:hover': {
                          bgcolor: isAddingRevision ? 'rgba(33, 150, 243, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                          borderColor: isAddingRevision ? '#2196f3' : 'rgba(0, 0, 0, 0.23)'
                        }
                      }}
                    >
                      {isAddingRevision ? 'Vyberte text' : 'Přidat revizi'}
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              {/* Global style for text selection */}
              {(isAddingComment || isAddingRevision) && (
                <style>
                  {`
                    #contract-preview-box ::selection {
                      background-color: ${isAddingComment ? '#ffeb3b' : '#90caf9'} !important;
                      color: #000 !important;
                    }
                    #contract-preview-box ::-moz-selection {
                      background-color: ${isAddingComment ? '#ffeb3b' : '#90caf9'} !important;
                      color: #000 !important;
                    }
                  `}
                </style>
              )}

              <Box
                id="contract-preview-box"
                ref={contractPreviewRef}
                onMouseUp={() => {
                  if (isAddingComment || isAddingRevision) {
                    const selection = window.getSelection();
                    if (selection && selection.toString().trim()) {
                      const selected = selection.toString();
                      const range = selection.getRangeAt(0);
                      const preSelectionRange = range.cloneRange();
                      preSelectionRange.selectNodeContents(contractPreviewRef.current!);
                      preSelectionRange.setEnd(range.startContainer, range.startOffset);
                      const start = preSelectionRange.toString().length;
                      const end = start + selected.length;
                      
                      setSelectedText(selected);
                      setSelectedTextRange({ start, end });
                      
                      if (isAddingComment) {
                        setShowCommentDialog(true);
                      } else if (isAddingRevision) {
                        setShowRevisionDialog(true);
                      }
                    }
                  }
                }}
                sx={{
                  maxHeight: 600,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 3,
                  py: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  bgcolor: '#fafafa',
                  position: 'relative',
                  cursor: (isAddingComment || isAddingRevision) ? 'text' : 'default',
                  userSelect: (isAddingComment || isAddingRevision) ? 'text' : 'auto',
                  '&::selection, & *::selection': {
                    backgroundColor: isAddingComment ? '#ffeb3b' : isAddingRevision ? '#90caf9' : '#b3d4fc',
                    color: '#000'
                  },
                  '&::-moz-selection, & *::-moz-selection': {
                    backgroundColor: isAddingComment ? '#ffeb3b' : isAddingRevision ? '#90caf9' : '#b3d4fc',
                    color: '#000'
                  },
                  '&::-webkit-scrollbar': {
                    width: 8
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'grey.100'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.400',
                    borderRadius: 4
                  }
                }}
              >
                {renderContractText()}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Comments and Revisions Panel - Right Side */}
        {(contract.comments.length > 0 || contract.revisions.length > 0) && (
          <Box sx={{ 
            flex: '0 0 300px',
            maxWidth: '300px',
            width: '300px',
            minWidth: 0,
            position: { lg: 'sticky' },
            top: { lg: 20 },
            alignSelf: 'flex-start',
            ml: 2
          }}>
            <Card sx={{ 
              height: { lg: 'calc(100vh - 120px)' },
              maxHeight: { xs: 600, lg: 'calc(100vh - 120px)' },
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}>
              <CardContent sx={{ 
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CommentIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Poznámky a revize
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {contract.comments.length} poznámek, {contract.revisions.length} revizí
                </Typography>
              </CardContent>
              <CardContent sx={{ 
                flex: 1,
                overflow: 'auto',
                p: 2
              }}>
                <Stack spacing={2}>
                  {/* Poznámky */}
                  {contract.comments.map((comment) => (
                    <Paper 
                      key={comment.id} 
                      onClick={() => handleCommentClick(comment)}
                      sx={{ 
                        p: 2, 
                        bgcolor: '#fffbf0',
                        border: '1px solid #ffc107',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.4s ease-out',
                        '&:hover': {
                          bgcolor: '#fff3cd',
                          boxShadow: '0 4px 8px rgba(255, 193, 7, 0.2)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CommentIcon sx={{ fontSize: 18, color: '#ffc107' }} />
                        <Typography variant="caption" sx={{ color: '#000' }}>
                          Poznámka - {new Date(comment.timestamp).toLocaleString('cs-CZ')}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', mb: 1 }}>
                        "{comment.selectedText.substring(0, 50)}{comment.selectedText.length > 50 ? '...' : ''}"
                      </Typography>
                      
                      <Typography variant="body2" sx={{ color: '#000' }}>
                        {comment.comment}
                      </Typography>
                      
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteComment(comment.id);
                        }}
                        sx={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: 'error.main',
                          opacity: 0.6,
                          '&:hover': { opacity: 1 }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                ))}

                  {/* Revize */}
                  {contract.revisions.map((revision) => (
                    <Paper 
                      key={revision.id} 
                      onClick={() => handleRevisionClick(revision)}
                      sx={{ 
                        p: 2, 
                        bgcolor: '#e3f2fd',
                        border: '1px solid #2196f3',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.4s ease-out',
                        '&:hover': {
                          bgcolor: '#bbdefb',
                          boxShadow: '0 4px 8px rgba(33, 150, 243, 0.2)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TrackChangesIcon sx={{ fontSize: 18, color: '#2196f3' }} />
                        <Typography variant="caption" sx={{ color: '#000' }}>
                          Revize - {new Date(revision.timestamp).toLocaleString('cs-CZ')}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ color: '#000', mb: 1 }}>
                        <strong>Důvod:</strong> {revision.reason}
                      </Typography>
                      
                      {revision.accepted === null && (
                        <Box sx={{ mt: 1 }}>
                          {/* Pokud je autor revize ze shared, zobraz pouze Upravit */}
                          {revision.authorSource === 'shared' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: Implementovat úpravu revize
                                alert('Upravit revizi - funkce bude doplněna');
                              }}
                              sx={{ 
                                fontSize: '0.75rem',
                                color: '#000',
                                borderColor: '#ccc',
                                '&:hover': { 
                                  borderColor: '#999',
                                  backgroundColor: 'rgba(0,0,0,0.04)'
                                }
                              }}
                            >
                              Upravit (vlastní revize)
                            </Button>
                          ) : (
                            /* Pokud je autor z originále nebo není specifikován, zobraz Přijmout/Odmítnout */
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmAcceptRevision(revision.id);
                                }}
                                sx={{ 
                                  fontSize: '0.75rem',
                                  bgcolor: 'success.main',
                                  '&:hover': { bgcolor: 'success.dark' }
                                }}
                              >
                                Přijmout
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRejectRevisionId(revision.id);
                                }}
                                sx={{ 
                                  fontSize: '0.75rem',
                                  color: 'error.main',
                                  borderColor: 'error.main',
                                  '&:hover': { 
                                    bgcolor: 'rgba(244, 67, 54, 0.08)',
                                    borderColor: 'error.dark'
                                  }
                                }}
                              >
                                Odmítnout
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {revision.accepted === true && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label="Přijato" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#4caf50',
                              color: '#fff'
                            }}
                          />
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowRevisionDetail(revision.id);
                            }}
                            sx={{ 
                              fontSize: '0.75rem',
                              color: '#000',
                              textTransform: 'none'
                            }}
                          >
                            Náhled
                          </Button>
                        </Box>
                      )}
                      
                      {revision.accepted === false && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label="Odmítnuto" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#f44336',
                              color: '#fff'
                            }}
                          />
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowRevisionDetail(revision.id);
                            }}
                            sx={{ 
                              fontSize: '0.75rem',
                              color: '#000',
                              textTransform: 'none'
                            }}
                          >
                            Náhled
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Dialogy */}
      <Dialog open={showCommentDialog} onClose={() => setShowCommentDialog(false)}>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Přidat poznámku</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>Vybraný text: "{selectedText}"</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Poznámka"
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowCommentDialog(false)} sx={{ color: '#000 !important' }}>
              Zrušit
            </Button>
            <Button variant="contained" onClick={handleAddComment} sx={{ color: '#000 !important' }}>
              Přidat
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={showRevisionDialog} onClose={() => setShowRevisionDialog(false)}>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Přidat revizi</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>Původní text: "{selectedText}"</Typography>
          <TextField
            fullWidth
            label="Nový text"
            value={newRevisionText}
            onChange={(e) => setNewRevisionText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Důvod změny"
            value={newRevisionReason}
            onChange={(e) => setNewRevisionReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowRevisionDialog(false)} sx={{ color: '#000 !important' }}>
              Zrušit
            </Button>
            <Button variant="contained" onClick={handleAddRevision} sx={{ color: '#000 !important' }}>
              Přidat
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmAcceptRevision !== null} onClose={() => setConfirmAcceptRevision(null)}>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Potvrdit přijetí revize</Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Opravdu chcete přijmout tuto revizi?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button onClick={() => setConfirmAcceptRevision(null)} sx={{ color: '#000 !important' }}>
              Ne
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={() => confirmAcceptRevision && handleAcceptRevision(confirmAcceptRevision)}
              sx={{ color: '#000 !important' }}
            >
              Ano, přijmout
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectRevisionId !== null} onClose={() => setRejectRevisionId(null)}>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Odmítnout revizi</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Důvod odmítnutí"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setRejectRevisionId(null)} sx={{ color: '#000 !important' }}>
              Zrušit
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={() => rejectRevisionId && handleRejectRevision(rejectRevisionId, rejectReason)}
              disabled={!rejectReason.trim()}
              sx={{ color: '#000 !important' }}
            >
              Odmítnout
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog pro detail revize */}
      <Dialog 
        open={showRevisionDetail !== null} 
        onClose={() => setShowRevisionDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {showRevisionDetail && (() => {
            const revision = contract.revisions.find(r => r.id === showRevisionDetail);
            if (!revision) return null;
            return (
              <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrackChangesIcon sx={{ fontSize: 32, color: '#2196f3' }} />
                  <Typography variant="h6">
                    Detail revize
                  </Typography>
                  {revision.accepted === true && (
                    <Chip label="Přijato" sx={{ bgcolor: '#4caf50', color: '#fff' }} size="small" />
                  )}
                  {revision.accepted === false && (
                    <Chip label="Odmítnuto" sx={{ bgcolor: '#f44336', color: '#fff' }} size="small" />
                  )}
                  {revision.accepted === null && (
                    <Chip label="Čeká" color="default" size="small" />
                  )}
                </Box>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Původní text:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#ffebee' }}>
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#d32f2f' }}>
                    {revision.originalText}
                  </Typography>
                </Paper>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Nový text:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                    {revision.newText}
                  </Typography>
                </Paper>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Důvod změny:
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="body2">
                    {revision.reason}
                  </Typography>
                </Paper>
                
                <Typography variant="caption" color="text.secondary">
                  Vytvořeno: {new Date(revision.timestamp).toLocaleString('cs-CZ')}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowRevisionDetail(null)}
                    sx={{ color: '#000 !important' }}
                  >
                    Zavřít
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>
      
      <style>
        {`
          @keyframes revision-pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
            }
            50% {
              box-shadow: 0 0 10px 5px rgba(255, 193, 7, 0.3);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
            }
          }
          
          .revision-highlight-pulse.revision-strikethrough {
            animation: revision-pulse 2s ease-in-out;
            background-color: rgba(255, 193, 7, 0.3) !important;
          }
          
          .revision-highlight-pulse.revision-replacement {
            animation: revision-pulse 2s ease-in-out;
            background-color: rgba(255, 193, 7, 0.3) !important;
          }
        `}
      </style>
      </Container>
    </Box>
  );
};

export default SharedContractPage;